/**
 * Union / Multi-Local Payroll layout route.
 *
 * Side-nav with links to the union-payroll sub-pages, gated by the
 * construction pack flag on the current entity. (The union sub-pack flag —
 * entities.union_enabled — is not yet exposed on the AvailableEntity
 * type; backend require_pack dependency enforces it on every request.)
 */
import { createFileRoute, Link, Outlet, useMatchRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";

import { myEntitiesQueryOptions } from "@/lib/queries";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l3")({
  component: PayrollL3Layout,
});

const SUB_NAV = [
  { to: "/payroll-l3", label: "Overview", end: true },
  { to: "/payroll-l3/cbas", label: "CBAs" },
  { to: "/payroll-l3/classification-queue", label: "Classification Queue" },
  { to: "/payroll-l3/fringe-funds", label: "Fringe Funds" },
  { to: "/payroll-l3/remittance", label: "Remittance" },
  { to: "/payroll-l3/apprentice-compliance", label: "Apprentice Compliance" },
  { to: "/payroll-l3/contracts", label: "Contracts" },
  { to: "/payroll-l3/memberships", label: "Employee Memberships" },
  { to: "/payroll-l3/compliance-alerts", label: "Compliance Alerts" },
] as const;

function PayrollL3Layout() {
  const matchRoute = useMatchRoute();
  const entitiesQuery = useQuery(myEntitiesQueryOptions());
  const currentEntity = entitiesQuery.data?.find((e) => e.is_current);
  const constructionEnabled = currentEntity?.construction_enabled ?? true;

  if (!constructionEnabled) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500 dark:text-slate-400">
        The Construction pack is not enabled for this entity.
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1">
      <aside className="w-52 shrink-0 border-r border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col py-4 px-2 gap-0.5">
        <p className="px-3 text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">
          Union Payroll
        </p>
        <div className="px-3 mb-2">
          <HelpHint docPath="payroll-l3/README.md" label="Union payroll overview" />
        </div>
        {SUB_NAV.map((item) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const active = matchRoute({ to: item.to as any, fuzzy: !item.end });
          return (
            <Link
              key={item.to}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              to={item.to as any}
              className={clsx(
                "px-3 py-2 rounded-md text-sm transition",
                active
                  ? "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 font-medium"
                  : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-800",
              )}
            >
              {item.label}
            </Link>
          );
        })}
      </aside>

      <main className="flex-1 overflow-auto p-6">
        <Outlet />
      </main>
    </div>
  );
}
